//frontend/src/Sidebar.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import './Sidebar.css';

const Sidebar = ({ collapsed, setCollapsed, navbarExpanded }) => {
    const { user } = useAuth();
    const location = useLocation();
    const [mobileShow, setMobileShow] = useState(false);
    const [notificationCount, setNotificationCount] = useState(0);
    const [messageCount, setMessageCount] = useState(0);
    const [pendingDossiersCount, setPendingDossiersCount] = useState(0);

    const loadCounts = useCallback(async () => {
        try {
            const notifResponse = await api.get('/notifications/');
            const notifData = notifResponse.data.notifications.data || [];
            const unreadNotifs = notifData.filter(n => !n.is_read).length;
            setNotificationCount(unreadNotifs);

            const messagesResponse = await api.get('/conversations/');
            const messagesData = messagesResponse.data.conversations || [];
            const totalUnread = messagesData.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
            setMessageCount(totalUnread);

            if (user?.role === 'admin' || user?.role === 'superadmin') {
                const dossiersResponse = await api.get('/admin/dossiers/statistics');
                const pending = (dossiersResponse.data.submitted || 0) + (dossiersResponse.data.in_review || 0);
                setPendingDossiersCount(pending);
            }
        } catch (error) {
            console.error('Error loading counts:', error);
        }
    }, [user?.role]);

    useEffect(() => {
        if (user) {
            loadCounts();
            const interval = setInterval(loadCounts, 30000);
            return () => clearInterval(interval);
        }
    }, [user, loadCounts]);

    useEffect(() => {
        const isMobile = window.innerWidth <= 768;
        if (isMobile) {
            setMobileShow(navbarExpanded);
        }
    }, [navbarExpanded]);

    const getInitials = (firstName, lastName) => {
        const first = firstName ? firstName.charAt(0) : '';
        const last = lastName ? lastName.charAt(0) : '';
        return `${first}${last}`.toUpperCase();
    };

    const getRoleLabel = (role) => {
        const roles = {
            user: 'Utilisateur',
            admin: 'Administrateur',
            superadmin: 'Super Admin'
        };
        return roles[role] || 'Utilisateur';
    };

    const isActive = (path) => {
        return location.pathname === path ? 'active' : '';
    };

    const userMenu = [
        { path: '/dashboard', icon: '📊', label: 'Tableau de bord' },
        { path: '/dossier/create', icon: '📝', label: 'Nouveau dossier' },
        { path: '/mes-dossiers', icon: '📁', label: 'Mes dossiers' },
        { path: '/notifications', icon: '🔔', label: 'Notifications', badge: notificationCount },
        { path: '/chat', icon: '💬', label: 'Chat / Assistance', badge: messageCount },
        { path: '/profile', icon: '👤', label: 'Mon profil' },
    ];

    const adminMenu = [
        { path: '/admin', icon: '📊', label: 'Tableau de bord' },
        { path: '/admin/users', icon: '👥', label: 'Utilisateurs' },
        { path: '/admin/dossiers', icon: '📋', label: 'Tous les dossiers' },
        { path: '/admin/pending', icon: '⏳', label: 'En attente', badge: pendingDossiersCount },
        { path: '/notifications', icon: '🔔', label: 'Notifications', badge: notificationCount },
        { path: '/admin/chat', icon: '💬', label: 'Messages', badge: messageCount },
    ];

    const superAdminMenu = [
        { path: '/superadmin', icon: '🔐', label: 'Tableau de bord' },
        { path: '/superadmin/admins', icon: '👨‍💼', label: 'Gestion admins' },
        { path: '/superadmin/users', icon: '👥', label: 'Tous utilisateurs' },
        { path: '/superadmin/dossiers', icon: '📋', label: 'Tous dossiers' },
        { path: '/notifications', icon: '🔔', label: 'Notifications', badge: notificationCount },
        { path: '/superadmin/chat', icon: '💬', label: 'Support chat', badge: messageCount },
        { path: '/superadmin/settings', icon: '⚙️', label: 'Paramètres' },
        { path: '/superadmin/logs', icon: '📝', label: 'Logs système' },
        { path: '/superadmin/stats', icon: '📊', label: 'Statistiques' },
        { path: '/superadmin/finance', icon: '💰', label: 'Finance' },
    ];

    const getMenuItems = () => {
        if (user?.role === 'superadmin') return superAdminMenu;
        if (user?.role === 'admin') return adminMenu;
        return userMenu;
    };

    const menuItems = getMenuItems();

    const isRegularUser = !user?.role || user?.role === 'user';
    const sidebarThemeClass = isRegularUser ? 'sidebar-white' : 'sidebar-dark';

    const handleToggle = () => {
        const isMobile = window.innerWidth <= 768;
        if (isMobile) {
            setMobileShow(!mobileShow);
        } else {
            setCollapsed(!collapsed);
        }
    };

    const sidebarClass = `sidebar ${sidebarThemeClass} ${collapsed && !mobileShow ? 'sidebar-collapsed' : ''} ${mobileShow ? 'show' : ''}`;

    return (
        <div className={sidebarClass}>
            <div className="sidebar-toggle" onClick={handleToggle}>
                {collapsed && !mobileShow ? '→' : '←'}
            </div>

            <div className="sidebar-header">
                <div className="sidebar-user">
                    <div className={`sidebar-avatar ${isRegularUser ? 'avatar-primary' : 'avatar-gradient'}`}>
                        {user?.photo ? (
                            <img src={user.photo} alt="Avatar" style={{ width: '100%', borderRadius: '50%' }} />
                        ) : (
                            getInitials(user?.first_name, user?.last_name)
                        )}
                    </div>
                    {!collapsed && (
                        <div className="sidebar-user-info">
                            <p className="sidebar-user-name">
                                {user?.first_name} {user?.last_name}
                            </p>
                            <p className="sidebar-user-role">
                                {getRoleLabel(user?.role)}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            <nav>
                <ul className="sidebar-nav">
                    {menuItems.map((item, index) => (
                        <li key={index} className="sidebar-nav-item">
                            <Link
                                to={item.path}
                                className={`sidebar-nav-link ${isActive(item.path)}`}
                                title={collapsed ? item.label : ''}
                                onClick={() => {
                                    const isMobile = window.innerWidth <= 768;
                                    if (isMobile) {
                                        setMobileShow(false);
                                    }
                                }}
                            >
                                <span className="sidebar-nav-icon">{item.icon}</span>
                                {!collapsed && (
                                    <>
                                        <span className="sidebar-nav-text">{item.label}</span>
                                        {item.badge > 0 && (
                                            <span className="badge bg-danger ms-auto">{item.badge}</span>
                                        )}
                                    </>
                                )}
                                {collapsed && item.badge > 0 && (
                                    <span className="sidebar-badge-collapsed">{item.badge}</span>
                                )}
                            </Link>
                        </li>
                    ))}
                </ul>

                {(user?.role === 'admin' || user?.role === 'superadmin') && (
                    <div className="sidebar-section">
                        {!collapsed && <div className="sidebar-section-title">OUTILS</div>}
                        <ul className="sidebar-nav">
                            <li className="sidebar-nav-item">
                                <Link to="/tools/export" className="sidebar-nav-link" onClick={() => {
                                    const isMobile = window.innerWidth <= 768;
                                    if (isMobile) {
                                        setMobileShow(false);
                                    }
                                }}>
                                    <span className="sidebar-nav-icon">📤</span>
                                    {!collapsed && <span className="sidebar-nav-text">Export données</span>}
                                </Link>
                            </li>
                            <li className="sidebar-nav-item">
                                <Link to="/tools/backup" className="sidebar-nav-link">
                                    <span className="sidebar-nav-icon">💾</span>
                                    {!collapsed && <span className="sidebar-nav-text">Sauvegarde</span>}
                                </Link>
                            </li>
                        </ul>
                    </div>
                )}
            </nav>
        </div>
    );
};

export default Sidebar;